/**
 * The op log: the replayable record of what a run did to its draft
 * (docs/agent.md §3).
 *
 * Why a semantic op log rather than a diff or a Yjs update: at apply time the
 * live timeline may have moved on. Replaying "trim clip X to 4.0s" on the
 * current edit either succeeds meaningfully or fails loudly ("the clip you
 * asked me to trim was deleted"). A CRDT merge would instead silently produce
 * something nobody asked for.
 */
import type { Op } from '../core/types';

/** Append a recorded mutating call. Returns a new array — the log is immutable. */
export function appendOp(log: Op[], op: Op): Op[] {
  return [...log, op];
}

/**
 * Read a dotted path out of an args object, supporting a single `*` segment to
 * mean "every element of this array" (e.g. 'clips.*').
 */
function readPath(args: unknown, path: string[]): unknown[] {
  if (!path.length) return [args];
  const [head, ...rest] = path;
  if (args === null || typeof args !== 'object') return [];
  if (head === '*') {
    if (!Array.isArray(args)) return [];
    return args.flatMap((v) => readPath(v, rest));
  }
  const next = (args as Record<string, unknown>)[head];
  return next === undefined ? [] : readPath(next, rest);
}

/** Rewrite a dotted path in place, mapping each addressed value through `fn`. */
function writePath(args: unknown, path: string[], fn: (v: unknown) => unknown): void {
  if (!path.length || args === null || typeof args !== 'object') return;
  const [head, ...rest] = path;
  if (head === '*') {
    if (!Array.isArray(args)) return;
    for (let i = 0; i < args.length; i++) {
      if (rest.length) writePath(args[i], rest, fn);
      else args[i] = fn(args[i]);
    }
    return;
  }
  const obj = args as Record<string, unknown>;
  if (!(head in obj)) return;
  if (rest.length) writePath(obj[head], rest, fn);
  else obj[head] = fn(obj[head]);
}

/** Every id-typed value in `args`, per the tool's declared id paths. */
export function collectIdArgs(
  args: Record<string, unknown>,
  idArgPaths: string[] = [],
): string[] {
  const out: string[] = [];
  for (const p of idArgPaths) {
    for (const v of readPath(args, p.split('.'))) {
      if (typeof v === 'string') out.push(v);
    }
  }
  return out;
}

/**
 * Rewrite id-typed args through `idMap`. Ids absent from the map are left as
 * they are: they refer to clips that already existed on the base edit, which is
 * exactly what should carry over unchanged. Ids the run itself created always
 * ARE in the map, so a stale draft id can never leak into a replayed op.
 */
export function remapIdArgs(
  args: Record<string, unknown>,
  idArgPaths: string[] = [],
  idMap: Map<string, string>,
): Record<string, unknown> {
  const clone = structuredClone(args);
  for (const p of idArgPaths) {
    writePath(clone, p.split('.'), (v) =>
      typeof v === 'string' && idMap.has(v) ? idMap.get(v) : v,
    );
  }
  return clone;
}

/** Ops are stored as plain JSON leaves in the CRDT; this is the round-trip. */
export function serializeOps(ops: Op[]): string {
  return JSON.stringify(ops);
}

export function deserializeOps(raw: string): Op[] {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isOp);
  } catch {
    return [];
  }
}

export function isOp(value: unknown): value is Op {
  if (value === null || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.tool === 'string' &&
    typeof o.args === 'object' &&
    o.args !== null &&
    !Array.isArray(o.args) &&
    Array.isArray(o.produced) &&
    (o.produced as unknown[]).every((p) => typeof p === 'string')
  );
}
